import { Controller } from "stimulus";
import { DEFAULT_TAB, KINDS, defaultTab, hasTabs, isSorted, matchesTab, matchesQuery, normalizeQuery, resultLabel, tally, tabLabel, summaryLabel, toggleLabel, emptyMessage, nextTab, placePanel, stateFor, tabsFor, viewportBounds } from "../boarding_calendar/movements";

const onmount = require("onmount");

const MOBILE_QUERY = "(max-width: 639.98px)";
const PANEL_HEIGHT = 680;
const LIST_SLACK = 48;
const IGNORED_CLICKS = ".popover, .tooltip, .modal, .dropdown-menu, .notyf";
const WIDGETS = '[data-toggle="tooltip"], [data-toggle="popover"], [data-toggle="popover-clickable"]';
const FACILITY_TAB = 'a[href="#in-facility"]';
const FACILITY_CARDS = "boarding-checkinout-date-paginator";
const NOT_CHECKED_IN = '[data-section="pending"]';
const CHECKED_IN = '[data-section="in"]';

let facilityStale = false;
let facilityHooked = false;

export default class extends Controller {
  static targets = ["tile", "panel"];
  static values = { inUrl: String, outUrl: String, facilityUrl: String };

  connect() {
    this.hookFacilityTab();
    this.views = {};
    this.panelTargets.forEach((panel) => {
      const variant = panel.dataset.variant;
      this.views[variant] = {
        variant,
        panel,
        kind: null,
        tab: DEFAULT_TAB,
        token: 0,
        cache: {},
        counts: null,
        ready: false,
        query: "",
        title: panel.querySelector('[data-mv="title"]'),
        list: panel.querySelector('[data-mv="list"]'),
        empty: panel.querySelector('[data-mv="empty"]'),
        summary: panel.querySelector('[data-mv="summary"]'),
        search: panel.querySelector('[data-mv="search"]'),
        input: panel.querySelector('[data-mv="query"]'),
        results: panel.querySelector('[data-mv="results"]'),
        clear: panel.querySelector('[data-mv="clear"]'),
        tabs: Array.from(panel.querySelectorAll('[data-mv="tab"]')),
      };
    });

    Object.values(this.views).forEach((view) => {
      view.panel.hidden = true;
      view.list.innerHTML = "";
      this.resetSearch(view);
      this.syncTiles(view);
    });

    this.mediaQuery = window.matchMedia(MOBILE_QUERY);
    this.boundClick = (event) => this.handleDocumentClick(event);
    this.boundKeydown = (event) => this.handleKeydown(event);
    this.boundAttendance = (event) => this.handleAttendance(event);
    this.boundBreakpoint = () => this.closeAll();
    this.boundListKeydown = (event) => this.handleListKeydown(event);
    this.boundResize = () => this.reposition();

    document.addEventListener("click", this.boundClick);
    window.addEventListener("resize", this.boundResize);
    document.addEventListener("keydown", this.boundKeydown);
    document.addEventListener("attendanceChanged", this.boundAttendance);
    if (this.mediaQuery.addEventListener) this.mediaQuery.addEventListener("change", this.boundBreakpoint);

    Object.values(this.views).forEach((view) => view.list.addEventListener("keydown", this.boundListKeydown));

    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.reposition());
      this.resizeObserver.observe(this.element);
    }
  }

  disconnect() {
    document.removeEventListener("click", this.boundClick);
    document.removeEventListener("keydown", this.boundKeydown);
    window.removeEventListener("resize", this.boundResize);
    document.removeEventListener("attendanceChanged", this.boundAttendance);
    if (this.mediaQuery.removeEventListener) this.mediaQuery.removeEventListener("change", this.boundBreakpoint);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    this.lockScroll(false);

    Object.values(this.views).forEach((view) => {
      view.token += 1;
      view.list.removeEventListener("keydown", this.boundListKeydown);
      this.disposeWidgets(view);
      this.forget(view);
    });
  }

  toggle(event) {
    const tile = event.currentTarget;
    const view = this.views[tile.dataset.variant];
    if (!view) return;

    if (view.kind === tile.dataset.kind) {
      this.closeView(view);
      return;
    }

    this.open(view, tile.dataset.kind);
    if (event.detail === 0) view.panel.focus();
  }

  close(event) {
    this.closeView(this.viewFor(event.currentTarget), { focus: true });
  }

  selectTab(event) {
    const view = this.viewFor(event.currentTarget);
    if (view) this.setTab(view, event.currentTarget.dataset.tab);
  }

  tabKeydown(event) {
    const view = this.viewFor(event.currentTarget);
    const tab = view && nextTab(view.tab, event.key, tabsFor(view.kind));
    if (!tab) return;

    event.preventDefault();
    this.setTab(view, tab);
    view.tabs.find((button) => button.dataset.tab === tab).focus();
  }

  search(event) {
    const view = this.viewFor(event.currentTarget);
    if (!view || !view.kind) return;

    view.query = normalizeQuery(event.currentTarget.value);
    this.syncSearch(view);
    if (!view.ready) return;

    this.refresh(view);
    view.list.scrollTop = 0;
  }

  searchKeydown(event) {
    const view = this.viewFor(event.currentTarget);
    if (!view) return;

    if (event.key === "Escape" && view.input.value !== "") {
      event.preventDefault();
      this.clearQuery(view);
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (view.variant === "mobile") view.input.blur();
    }
  }

  keepFocus(event) {
    event.preventDefault();
  }

  clearSearch(event) {
    const view = this.viewFor(event.currentTarget);
    if (!view) return;

    this.clearQuery(view);
    view.input.focus();
  }

  clearQuery(view) {
    this.resetSearch(view);
    if (view.ready) this.refresh(view);
    view.list.scrollTop = 0;
  }

  resetSearch(view) {
    view.query = "";
    view.input.value = "";
    this.syncSearch(view);
  }

  syncSearch(view, visible = null) {
    const active = view.query !== "";
    view.search.classList.toggle("is-filled", active);
    view.clear.hidden = !active;
    view.results.textContent = active && visible !== null ? resultLabel(visible) : "";
  }

  retry(event) {
    const view = this.views[event.currentTarget.dataset.variant];
    if (view && view.kind) this.load(view);
  }

  open(view, kind) {
    this.stash(view);

    view.kind = kind;
    view.tab = hasTabs(kind) ? defaultTab(kind) : "all";
    view.counts = null;
    view.ready = false;
    view.panel.dataset.kind = kind;
    view.panel.hidden = false;
    view.title.textContent = KINDS[kind].title;
    view.list.setAttribute("aria-label", KINDS[kind].title);
    view.list.setAttribute("role", hasTabs(kind) ? "tabpanel" : "region");
    this.resetSearch(view);

    this.syncTiles(view);
    this.reposition();
    if (view.variant === "mobile") {
      this.lockScroll(true);
      view.panel.focus({ preventScroll: true });
    }
    if (!this.restore(view)) this.load(view);
  }

  closeView(view, { focus = false } = {}) {
    if (!view || !view.kind) return;

    const kind = view.kind;
    view.token += 1;
    this.stash(view);
    view.kind = null;
    view.ready = false;
    view.panel.hidden = true;
    if (view.variant === "mobile") this.lockScroll(false);
    this.resetSearch(view);
    this.syncTiles(view);

    if (focus) {
      const tile = this.tilesFor(view).find((candidate) => candidate.dataset.kind === kind);
      if (tile) tile.focus();
    }
  }

  stash(view) {
    if (view.kind && view.ready) {
      this.hideWidgets(view);
      const fragment = document.createDocumentFragment();
      while (view.list.firstChild) fragment.appendChild(view.list.firstChild);
      view.cache[view.kind] = fragment;
    } else {
      this.disposeWidgets(view);
    }
    view.list.innerHTML = "";
  }

  restore(view) {
    const fragment = view.cache[view.kind];
    if (!fragment) return false;

    delete view.cache[view.kind];
    view.token += 1;
    view.list.innerHTML = "";
    view.list.appendChild(fragment);
    view.ready = true;
    this.refresh(view);
    this.reposition();
    return true;
  }

  forget(view, keep = null) {
    const $ = window.$;
    Object.keys(view.cache).forEach((kind) => {
      if (kind === keep) return;

      if ($) {
        const $widgets = $(view.cache[kind]).find(WIDGETS);
        $widgets.tooltip("dispose");
        $widgets.popover("dispose");
      }
      delete view.cache[kind];
    });
  }

  closeAll() {
    Object.values(this.views).forEach((view) => this.closeView(view));
  }

  load(view) {
    const token = ++view.token;
    const url = { in: this.inUrlValue, out: this.outUrlValue, facility: this.facilityUrlValue }[view.kind];

    view.ready = false;
    view.empty.hidden = true;
    view.summary.textContent = "";
    this.renderTabs(view);
    view.list.innerHTML = '<div class="bc-mv-loading"><span class="spinner-border spinner-border-sm text-primary" role="status"><span class="sr-only">Loading</span></span></div>';

    fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" }, credentials: "same-origin" })
      .then((response) => {
        if (!response.ok || response.redirected) throw new Error(`Request failed (${response.status})`);
        return response.text();
      })
      .then((html) => {
        if (token !== view.token) return;
        this.render(view, html);
      })
      .catch(() => {
        if (token !== view.token) return;
        this.showError(view);
      });
  }

  render(view, html) {
    view.list.innerHTML = html;

    if (this.rowsOf(view).length === 0) {
      view.list.innerHTML = "";
    } else {
      this.prepareContent(view);
    }

    view.ready = true;
    this.refresh(view);
    this.reposition();
  }

  showError(view) {
    view.list.innerHTML = "";
    view.empty.hidden = true;

    const box = document.createElement("div");
    box.className = "bc-mv-error";
    box.append("This list could not be loaded. ");

    const retry = document.createElement("button");
    retry.type = "button";
    retry.className = "bc-mv-retry";
    retry.dataset.variant = view.variant;
    retry.dataset.action = "boarding-movements#retry";
    retry.textContent = "Try again";
    box.append(retry);

    view.list.append(box);
    if (window.notyf) window.notyf.error("Something went wrong when loading your request. Please try again.");
  }

  prepareContent(view) {
    const $ = window.$;

    if ($) {
      const $list = $(view.list);
      $list.find('[data-toggle="tooltip"]').tooltip({ sanitize: false, container: "body", boundary: "viewport" });
      $list.find('[data-toggle="popover"]').popover({ sanitize: false, container: "body", boundary: "viewport" });
      $list
        .find('[data-toggle="popover-clickable"]')
        .popover({ sanitize: false, trigger: "manual", container: "body", boundary: "viewport" })
        .on("mouseenter", function () {
          if (!$(this).data("pinned")) $(this).popover("show");
        })
        .on("mouseleave", function () {
          if (!$(this).data("pinned")) $(this).popover("hide");
        })
        .on("click", function (event) {
          event.preventDefault();
          const pinned = $(this).data("pinned");
          $(view.list).find('[data-toggle="popover-clickable"]').data("pinned", false).popover("hide");
          if (!pinned) $(this).data("pinned", true).popover("show");
        });
    }

    onmount();

    view.list.querySelectorAll(".js-attendance-checkin-toggle").forEach((toggle) => {
      toggle.setAttribute("role", "button");
      toggle.tabIndex = 0;
    });
  }

  hideWidgets(view) {
    const $ = window.$;
    if (!$ || !view.list) return;

    const $widgets = $(view.list).find(WIDGETS);
    $widgets.tooltip("hide");
    $widgets.filter('[data-toggle="popover-clickable"]').data("pinned", false);
    $widgets.popover("hide");
  }

  disposeWidgets(view) {
    const $ = window.$;
    if (!$ || !view.list) return;

    const $widgets = $(view.list).find(WIDGETS);
    $widgets.tooltip("dispose");
    $widgets.popover("dispose");
  }

  handleAttendance(event) {
    const panel = event.target.closest && event.target.closest(".bc-mv-panel");
    const view = panel && this.element.contains(panel) && this.views[panel.dataset.variant];

    Object.values(this.views).forEach((other) => this.forget(other, other === view ? view.kind : null));
    if (!view || !view.kind) return;

    facilityStale = true;
    this.refresh(view, { filter: false });
  }

  hookFacilityTab() {
    const $ = window.$;
    if (facilityHooked || !$) return;

    facilityHooked = true;
    $(document).on("shown.bs.tab", FACILITY_TAB, () => {
      if (!facilityStale) return;

      facilityStale = false;
      document.querySelectorAll(`[data-controller~="${FACILITY_CARDS}"]`).forEach((card) => {
        const cards = this.application.getControllerForElementAndIdentifier(card, FACILITY_CARDS);
        if (cards) cards.loadContent();
      });
    });
  }

  handleDocumentClick(event) {
    const target = event.target;
    if (!target || !target.isConnected) return;

    Object.values(this.views).forEach((view) => {
      if (!view.kind || view.panel.contains(target)) return;
      if (this.tilesFor(view).some((tile) => tile.contains(target))) return;
      if (target.closest(IGNORED_CLICKS)) return;

      this.closeView(view);
    });

    const $ = window.$;
    if ($ && !$(target).closest('[data-toggle="popover-clickable"], .popover').length) {
      $(this.element).find('[data-toggle="popover-clickable"]').data("pinned", false).popover("hide");
    }
  }

  handleKeydown(event) {
    if (document.querySelector(".modal.show")) return;

    if (event.key === "Tab") {
      this.trapFocus(event);
      return;
    }

    if (event.key !== "Escape" || event.defaultPrevented) return;

    Object.values(this.views).forEach((view) => this.closeView(view, { focus: true }));
  }

  handleListKeydown(event) {
    if (event.key !== "Enter" && event.key !== " ") return;

    const toggle = event.target.closest && event.target.closest(".js-attendance-checkin-toggle");
    if (!toggle) return;

    event.preventDefault();
    toggle.click();
  }

  setTab(view, tab) {
    view.tab = tab;
    this.refresh(view);
    view.list.scrollTop = 0;
  }

  refresh(view, { filter = true } = {}) {
    if (!view.kind || !view.ready) return;

    if (filter && view.kind === "facility") this.moveCheckedIn(view);
    const rows = this.rowsOf(view);
    const states = rows.map((row) => this.stateOf(row));
    const counted = rows.filter((row) => !row.closest(NOT_CHECKED_IN));
    const bookings = new Set(counted.map((row) => row.dataset.bookingId)).size;

    view.counts = tally(view.kind, states);
    view.summary.textContent = summaryLabel(counted.length, bookings, { sorted: view.variant !== "mobile" && isSorted(view.kind), notCheckedIn: rows.length - counted.length });
    this.renderTabs(view);
    if (hasTabs(view.kind)) this.syncTileCount(view.kind, view.counts.all);

    rows.forEach((row, index) => {
      const toggle = row.querySelector(".js-attendance-checkin-toggle");
      if (toggle) toggle.setAttribute("aria-label", toggleLabel(row.dataset.petName, states[index]));
      if (filter) {
        row.classList.toggle("bc-mv-hide", !matchesTab(view.kind, view.tab, states[index]));
        row.classList.toggle("bc-mv-miss", !matchesQuery([row.dataset.petName, row.dataset.ownerName, row.dataset.serviceName], view.query));
      }
    });

    this.syncSections(view);
    const visible = this.visibleRows(view).length;
    this.syncSearch(view, visible);
    this.renderEmpty(view, visible);
    this.fitList(view);
  }

  moveCheckedIn(view) {
    const pending = view.list.querySelector(NOT_CHECKED_IN);
    const section = view.list.querySelector(CHECKED_IN);
    if (!pending || !section) return;

    const arrived = Array.from(pending.querySelectorAll(".js-checkin-checkout-row")).filter((row) => this.stateOf(row) === "in");
    if (arrived.length === 0) return;

    let body = section.querySelector("tbody");
    if (!body) {
      const wrapper = document.createElement("div");
      wrapper.style.containerType = "inline-size";
      const table = document.createElement("table");
      table.className = "table";
      body = document.createElement("tbody");
      table.appendChild(body);
      wrapper.appendChild(table);
      section.querySelectorAll(".bc-mv-section-empty").forEach((node) => node.remove());
      section.appendChild(wrapper);
    }
    arrived.forEach((row) => body.appendChild(row));
  }

  syncSections(view) {
    const filtering = normalizeQuery(view.query) !== "" || view.tab !== "all";

    view.list.querySelectorAll(".bc-mv-section").forEach((section) => {
      const rows = Array.from(section.querySelectorAll(".js-checkin-checkout-row"));
      const shown = rows.filter((row) => !row.classList.contains("bc-mv-miss") && !row.classList.contains("bc-mv-hide")).length;
      const blank = rows.length === 0 && !section.querySelector(".bc-mv-section-empty");
      section.classList.toggle("bc-mv-miss", blank || (filtering && shown === 0));

      const count = section.querySelector(".bc-mv-section-count");
      if (count) count.textContent = shown;
    });
  }

  visibleRows(view) {
    return this.rowsOf(view).filter((row) => !row.classList.contains("bc-mv-hide") && !row.classList.contains("bc-mv-miss"));
  }

  fitList(view) {
    const list = view.list;
    list.style.maxHeight = "";
    if (view.panel.hidden) return;

    const max = parseFloat(window.getComputedStyle(list).maxHeight);
    const overflow = list.scrollHeight - max;
    if (Number.isFinite(max) && overflow > 0 && overflow <= LIST_SLACK) list.style.maxHeight = `${list.scrollHeight}px`;
  }

  renderTabs(view) {
    if (!hasTabs(view.kind)) return;

    const container = view.tabs[0] && view.tabs[0].parentElement;
    tabsFor(view.kind).forEach((tab) => {
      const button = view.tabs.find((candidate) => candidate.dataset.tab === tab);
      if (button && container) container.appendChild(button);
    });

    view.tabs.forEach((button) => {
      const active = button.dataset.tab === view.tab;
      button.textContent = tabLabel(view.kind, button.dataset.tab, view.counts);
      button.setAttribute("aria-selected", active ? "true" : "false");
      button.tabIndex = active ? 0 : -1;
      button.classList.toggle("active", active);
    });
  }

  renderEmpty(view, visible) {
    if (!view.counts || visible > 0) {
      view.empty.hidden = true;
      return;
    }

    view.empty.textContent = emptyMessage(view.kind, view.tab, view.counts, view.query);
    view.empty.hidden = false;
  }

  syncTiles(view) {
    this.tilesFor(view).forEach((tile) => {
      const open = view.kind === tile.dataset.kind;
      tile.setAttribute("aria-expanded", open ? "true" : "false");
      tile.classList.toggle("bc-mv-tile-open", open);
    });
  }

  syncTileCount(kind, count) {
    this.tileTargets
      .filter((tile) => tile.dataset.kind === kind)
      .forEach((tile) => {
        const label = tile.querySelector('[data-mv="count"]');
        if (label) label.textContent = count;
      });
  }

  reposition() {
    const view = this.views.desktop;
    if (!view || !view.kind || this.mediaQuery.matches) return;

    const group = this.element.querySelector(".bc-now") || this.element.querySelector(".bc-capacity");
    if (!group || !group.offsetParent) return;

    const anchor = group.getBoundingClientRect();
    const container = this.element.getBoundingClientRect();
    const placement = placePanel({
      anchor,
      container: { left: container.left, top: container.top, bottom: container.bottom, width: container.width },
      viewport: viewportBounds(),
      panelWidth: view.panel.offsetWidth,
      panelHeight: PANEL_HEIGHT,
    });

    view.panel.style.maxHeight = `${placement.maxHeight}px`;
    view.panel.style.left = `${placement.left}px`;
    view.panel.style.top = placement.side === "down" ? `${placement.top}px` : "auto";
    view.panel.style.bottom = placement.side === "up" ? `${placement.bottom}px` : "auto";
    view.panel.dataset.side = placement.side;
  }

  lockScroll(on) {
    document.body.classList.toggle("bc-mv-modal-open", on);
  }

  trapFocus(event) {
    const view = this.views.mobile;
    if (!view || !view.kind) return;

    const focusable = Array.from(view.panel.querySelectorAll('a[href], button, input, [tabindex]')).filter((node) => !node.disabled && !node.hidden && node.tabIndex >= 0 && node.offsetParent !== null);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === view.panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !view.panel.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  }

  rowsOf(view) {
    return Array.from(view.list.querySelectorAll(".js-checkin-checkout-row"));
  }

  stateOf(row) {
    if (row.closest(CHECKED_IN) && !row.querySelector(".js-attendance-checkin-toggle")) return "in";
    const shown = (name) => !!row.querySelector(`.js-attendance-icon.${name}:not(.d-none)`);
    return stateFor({ checkedIn: shown("checked-in"), checkedOut: shown("checked-out") });
  }

  tilesFor(view) {
    return this.tileTargets.filter((tile) => tile.dataset.variant === view.variant);
  }

  viewFor(element) {
    const panel = element.closest(".bc-mv-panel");
    return panel ? this.views[panel.dataset.variant] : null;
  }
}
